import React, { useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2, X } from "lucide-react";

/**
 * Drag & drop / click-to-upload image uploader.
 * Props:
 *   value: string[] — array of image URLs (paths returned from /api/uploads/image or external URLs).
 *   onChange: (urls: string[]) => void
 *   kind: 'product' | 'review' | 'blog' (default 'product')
 *   max: number (default 8)
 */
export default function ImageUploader({ value = [], onChange, kind = "product", max = 8, testIdPrefix = "img-upload" }) {
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const inputRef = useRef(null);
  const dropRef = useRef(null);

  const filesToUrls = async (files) => {
    if (!files || !files.length) return;
    const slots = Math.max(0, max - value.length);
    const items = Array.from(files).slice(0, slots);
    if (files.length > slots) toast.error(`Only ${slots} more image${slots === 1 ? "" : "s"} allowed.`);
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of items) {
        if (!/^image\//.test(f.type)) { toast.error(`${f.name}: not an image`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        try {
          const { data } = await api.post(`/uploads/image?kind=${kind}`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          uploaded.push(`${BACKEND_URL}${data.url}`);
        } catch (e) {
          toast.error(`${f.name}: ${e?.response?.data?.detail || "upload failed"}`);
        }
      }
      if (uploaded.length) onChange([...value, ...uploaded]);
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("ring-2", "ring-[#5C1A1B]");
    filesToUrls(e.dataTransfer.files);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("ring-2", "ring-[#5C1A1B]");
  };
  const onDragLeave = (e) => e.currentTarget.classList.remove("ring-2", "ring-[#5C1A1B]");

  const addByUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    if (value.length >= max) { toast.error(`Max ${max} images.`); return; }
    onChange([...value, u]);
    setUrlInput("");
  };

  const remove = (i) => onChange(value.filter((_, j) => j !== i));

  return (
    <div data-testid={testIdPrefix}>
      <div
        ref={dropRef}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-[#BCA37F]/60 hover:border-[#5C1A1B] transition-colors cursor-pointer bg-[#F3EFEA]/30 p-6 text-center flex flex-col items-center gap-2"
        data-testid={`${testIdPrefix}-dropzone`}
      >
        {uploading ? <Loader2 className="animate-spin text-[#5C1A1B]" /> : <UploadCloud size={22} className="text-[#5C1A1B]" strokeWidth={1.4} />}
        <p className="text-sm text-[#1A1A1A]">Drop images here, or <span className="text-[#5C1A1B] underline">browse</span></p>
        <p className="text-[11px] uppercase tracking-nav text-[#7A7A7A]">JPG · PNG · WebP · max 10 MB · up to {max}</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={(e) => filesToUrls(e.target.files)}
          data-testid={`${testIdPrefix}-input`}
        />
      </div>

      <div className="mt-3 flex gap-2">
        <input
          placeholder="…or paste an image URL"
          className="flex-1 border border-black/10 px-3 py-2 text-sm"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addByUrl())}
          data-testid={`${testIdPrefix}-url-input`}
        />
        <button type="button" onClick={addByUrl} className="px-3 py-2 border border-[#5C1A1B] text-[#5C1A1B] text-[11px] uppercase tracking-nav" data-testid={`${testIdPrefix}-url-add`}>Add URL</button>
      </div>

      {value.length > 0 && (
        <div className="mt-4 grid grid-cols-4 md:grid-cols-6 gap-3">
          {value.map((src, i) => (
            <div key={i} className="relative group" data-testid={`${testIdPrefix}-thumb-${i}`}>
              <img src={src} alt="" className="w-full aspect-[3/4] object-cover border border-black/5" />
              <button
                type="button"
                onClick={() => remove(i)}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#1A1A1A] text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                aria-label="Remove image"
                data-testid={`${testIdPrefix}-thumb-remove-${i}`}
              >
                <X size={12} />
              </button>
              {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] uppercase tracking-nav bg-[#5C1A1B] text-white px-1.5 py-0.5">Cover</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
